import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { DateTime } from 'luxon';
import PolicyRow from '../PolicyRow';
import EditorLabel from '../../Editor/EditorLabel';
import EditorContainer from '../../Editor/EditorContainer';
import EditorButton from '../../Editor/EditorButton';
import { Card } from '../../card';

import { ButtonDropdown } from '../../v2';
import { FreeCondition, SimpleButton } from '..';
import Tab from './tab';
import { Tooltip, ButtonLink } from '../..';
import { convertV1PolicyEditorUiToV2 } from '../../policy/util';
import { ReactComponent as TimeIcon } from '../../../assets/timeIcon.svg';
import { ReactComponent as CalendarIcon } from '../../../assets/calendarIcon.svg';
import { ReactComponent as InfoIcon } from '../../../assets/icon-info.svg';
import baseTheme from '../../../theme';

const TIME_BASED_POLICY_DOCUMENTATION_LINK = 'https://developers.liveperson.com/conversation-orchestrator-dynamic-routing-advanced-routing.html';

const PolicyConditionSection = (props) => {
  const {
    temporaryPolicy,
    isEditing,
    customAttributes,
    agents,
    skills,
    intents,
    channels,
    validateObject,
    isComplexConditionEditor,
    onClickAdvancedCondition,
    onClickSimpleCondition,
    setTemporaryPolicy,
  } = props;

  const addNewRow = () => {
    const newObject = { ...temporaryPolicy };

    // add a new empty item to the temporaryPolicy based on the sectionType
    const newRow = { value: '', type: 'EQUAL', property: '' };
    const newItems = temporaryPolicy.conditions.concat(newRow);

    newObject.conditions = newItems;
    validateObject(newObject);
  };

  const addTimeRangeCondition = () => {
    // add a new empty item to the temporaryPolicy based on the sectionType
    const timeBasedCondition = "isTimeRange('00:00:00', '23:59:59', 'America/New_York', ['SAT', 'SUN'])";
    const newCondition = temporaryPolicy.condition.length !== 0
      ? `${temporaryPolicy.condition} and ${timeBasedCondition}` : timeBasedCondition;
    validateObject({ ...temporaryPolicy, condition: newCondition });
    setTemporaryPolicy({
      ...temporaryPolicy,
      condition: newCondition,
    });
  };

  const addDateRangeCondition = () => {
    // add a new empty item to the temporaryPolicy based on the sectionType
    const dateBasedCondition = `isDateRange('${DateTime.now().startOf('month').toFormat('MM/dd/yyyy')}', '${DateTime.now().endOf('month').toFormat('MM/dd/yyyy')}', 'America/New_York', ['SAT', 'SUN'])`;
    const newCondition = temporaryPolicy.condition.length !== 0
      ? `${temporaryPolicy.condition} and ${dateBasedCondition}` : dateBasedCondition;
    validateObject({ ...temporaryPolicy, condition: newCondition });
    setTemporaryPolicy({
      ...temporaryPolicy,
      condition: newCondition,
    });
  };

  const textAreaChange = (condition) => {
    validateObject({ ...temporaryPolicy, condition: condition.trim() });
    setTemporaryPolicy({ ...temporaryPolicy, condition });
  };

  const rows = (temporaryPolicy && temporaryPolicy.conditions) || [];

  const conditionEditorTypeTab = () => (
    <Flex pb={4} data-testid="condition-editor-type-tab">
      <SimpleButton
        onClick={onClickSimpleCondition}
        condition={temporaryPolicy.condition}
        isComplexConditionEditor={isComplexConditionEditor}
      />
      <Tab variant="tertiary" onClick={onClickAdvancedCondition} active={isComplexConditionEditor} style={{ marginLeft: '24px' }}>
        <Flex alignItems="center">
          <Text mr={1}>Write an expression</Text>
          <Tooltip position="top" arrow="center" parent={<Box marginTop={1}><InfoIcon /></Box>}>
            Customize and create complex policies with expressions
          </Tooltip>
        </Flex>
      </Tab>
    </Flex>
  );

  const dropdownData = [
    <Flex width={200} height={32} alignItems="center" onClick={addTimeRangeCondition}>
      <Box ml={2}>
        <TimeIcon />
      </Box>
      <Text fontSize={15} ml={2}>Based on time range</Text>
    </Flex>,
    <Flex width={200} height={32} alignItems="center" onClick={addDateRangeCondition}>
      <Box ml={2}>
        <CalendarIcon />
      </Box>
      <Text fontSize={15} ml={2}>Based on date range</Text>
    </Flex>,
  ];

  const editor = () => (
    isComplexConditionEditor ? (
      <>
        <FreeCondition
          placeholder="Enter expression"
          isEditing={isEditing}
          condition={temporaryPolicy.condition}
          onChange={(condition) => {
            textAreaChange(condition);
          }}
        />
        <ButtonDropdown data={dropdownData} />
      </>
    ) : (
      <>
        {rows.length > 0 && (
          <EditorContainer variant="inner">
            {rows.map(
              (item, index) => (
                <PolicyRow
                  key={index}
                  rowIndex={index}
                  rowItem={item}
                  isEditing={isEditing}
                  temporaryPolicy={temporaryPolicy}
                  sectionType="conditions"
                  customAttributes={customAttributes}
                  agents={agents}
                  skills={skills}
                  intents={intents}
                  channels={channels}
                  isLast={index + 1 === temporaryPolicy.conditions.length}
                  validateObject={validateObject}
                />
              ),
            )}
          </EditorContainer>
        )}
        <Flex>
          {isEditing && <EditorButton onClick={addNewRow} testid="add-condition-row">Add condition</EditorButton>}
        </Flex>
      </>
    ));

  return (
    <Box
      mt={2}
      p={2}
      pb={0}
      ml={1}
      data-testid="editor-section-conditions"
    >
      <EditorLabel largeSized={isEditing}>Conditions</EditorLabel>
      {isEditing && (
        <Flex alignItems="baseline" justifyContent="space-between">
          {conditionEditorTypeTab()}
          {isComplexConditionEditor
            && (
              <ButtonLink variant="inlineLink" link={TIME_BASED_POLICY_DOCUMENTATION_LINK}>
                Learn what we support
              </ButtonLink>
            )}
        </Flex>
      )}
      {!isEditing ? (
        <Card p={3}>
          <Text fontSize={13} lineHeight={baseTheme.lineHeights.policyTextarea}>
            {temporaryPolicy.condition}
          </Text>
        </Card>
      ) : (
        editor()
      )}
    </Box>
  );
};

PolicyConditionSection.defaultProps = {
  temporaryPolicy: undefined,
  customAttributes: [],
  skills: [],
  agents: [],
  intents: [],
  channels: [],
  isComplexConditionEditor: false,
  onClickAdvancedCondition: () => { },
  onClickSimpleCondition: () => { },
  setTemporaryPolicy: () => { },
};

PolicyConditionSection.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  temporaryPolicy: PropTypes.object,
  customAttributes: PropTypes.array,
  skills: PropTypes.array,
  agents: PropTypes.array,
  intents: PropTypes.array,
  channels: PropTypes.array,
  validateObject: PropTypes.func.isRequired,
  isComplexConditionEditor: PropTypes.bool,
  onClickAdvancedCondition: PropTypes.func,
  onClickSimpleCondition: PropTypes.func,
  setTemporaryPolicy: PropTypes.func,
};

export default PolicyConditionSection;
